import type { QuestionVoice } from '@repo/lib/quiz/questions/class/QuestionVoice';

import { base64ToBlob } from '@repo/lib/fs/base64ToBlob';
import { useMemo } from 'react';

import type { QuestionVoiceProps } from '@/components/features/quiz/question/question-resolver/question-voice';

import { useQuestionState } from '@/hooks/questions';

export function useQuestionVoice(questionVoiceProps: QuestionVoiceProps) {
  const { question, sectionIndex, questionIndex } = questionVoiceProps;

  const { setAnswer, getAnswer, getMarked, exportedAnswer, resetAnswer } = useQuestionState<QuestionVoice>(question, sectionIndex, questionIndex);

  const marked = getMarked();
  const exportedUserAnswer = exportedAnswer();

  const audioBlob = useMemo<Blob | null>(() => {
    const value = getAnswer();
    if (!value?.answer) {
      return null;
    }
    try {
      const parts: string[] = value.answer.split(',');
      const mimeMatch = parts[0]?.match(/:(.*?);/);
      if (!mimeMatch || parts.length < 2) {
        console.error('Invalid Data URL format for Blob conversion.');
        return null;
      }
      const mimeType: string | undefined = mimeMatch[1];
      const b64data: string | undefined = parts[1];

      if (!b64data || !mimeType) {
        return null;
      }

      return base64ToBlob(b64data, mimeType);
    } catch (error) {
      console.error('Error converting Data URL to Blob:', error);
      return null;
    }
  }, [getAnswer]);

  const handleValueChange = async (audioBlob: Blob | null) => {
    if (!audioBlob) {
      resetAnswer();
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64Str = reader.result as string;
      setAnswer(base64Str);
    };

    reader.onerror = (readError) => {
      console.error('FileReader error:', readError);
    };

    reader.readAsDataURL(audioBlob);
  };

  return {
    marked,
    audioBlob,
    exportedUserAnswer,
    handleValueChange,
  };
}
